import React from 'react';
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const EnhancedLoader = ({ 
  type = 'spinner', 
  size = 'medium', 
  text = 'Loading...', 
  showText = true,
  className = '' 
}) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-6 w-6',
    large: 'h-8 w-8',
    xlarge: 'h-12 w-12'
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
    xlarge: 'text-xl'
  };

  const iconClass = `${sizeClasses[size]} ${className}`;

  const renderIcon = () => {
    switch (type) {
      case 'spinner':
        return <Loader2 className={`${iconClass} animate-spin text-blue-600`} />;
      case 'success':
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case 'error':
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      case 'refresh':
        return <RefreshCw className={`${iconClass} animate-spin text-gray-600`} />;
      default:
        return <Loader2 className={`${iconClass} animate-spin text-blue-600`} />;
    }
  };

  return (
    <div className="flex items-center justify-center space-x-2">
      {renderIcon()}
      {showText && (
        <span className={`${textSizeClasses[size]} text-gray-600 font-medium`}>
          {text}
        </span>
      )}
    </div>
  );
};

// Full page loader
export const PageLoader = ({ text = 'Loading page...' }) => (
  <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
    <div className="text-center">
      <EnhancedLoader type="spinner" size="xlarge" text={text} />
    </div>
  </div>
);

// Inline loader for buttons
export const ButtonLoader = ({ text = 'Loading...' }) => (
  <div className="flex items-center space-x-2">
    <Loader2 className="h-4 w-4 animate-spin" />
    <span className="text-sm">{text}</span>
  </div>
);

// Overlay loader for modals/content
export const OverlayLoader = ({ text = 'Loading...', show = true }) => {
  if (!show) return null;
  
  return (
    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
      <EnhancedLoader type="spinner" size="large" text={text} />
    </div>
  );
};

// Progress loader with percentage
export const ProgressLoader = ({ progress = 0, text = 'Loading...' }) => (
  <div className="w-full">
    <div className="flex justify-between items-center mb-2">
      <span className="text-sm font-medium text-gray-700">{text}</span>
      <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  </div>
);

// Status loader for different states
export const StatusLoader = ({ 
  status = 'loading', 
  loadingText = 'Loading...',
  successText = 'Success!',
  errorText = 'Error occurred',
  onRetry 
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return { type: 'success', text: successText };
      case 'error':
        return { type: 'error', text: errorText };
      default:
        return { type: 'spinner', text: loadingText };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="flex flex-col items-center space-y-3">
      <EnhancedLoader {...config} size="large" />
      {status === 'error' && onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

// Dots loader for subtle loading indication
export const DotsLoader = ({ text = 'Loading' }) => (
  <div className="flex items-center space-x-1">
    <span className="text-gray-600">{text}</span>
    <div className="flex space-x-1">
      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

export default EnhancedLoader;



